/**NosAKEvidenceVault — /nosak/evidence — Evidence Vault & Registry */
import React from "react";
import { useNosak } from "@/context/NOSAKContext";
import { SectionCard } from "@/components/ds";
import { Shield, CheckCircle2, AlertTriangle, Info, FileText } from "lucide-react";
const fmtN=(v,u="")=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e3)return`${(n/1e3).toFixed(0)}K ${u}`.trim();return`${n.toLocaleString()} ${u}`.trim();};
export default function NosAKEvidenceVault() {
  const {group,subsidiaries,loading,selectedPeriod}=useNosak();
  if(loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;
  const conf=group.data_confidence_avg||0;
  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">Nosak Group</span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">Evidence Vault & Registry</h1>
        <p className="text-xs text-slate-400 mt-0.5">Evidence quality · MRV readiness · VVB preparation · Audit trail · {selectedPeriod}</p>
      </div>
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Portfolio Evidence Status</p>
        <div className="flex items-end gap-6 mb-3">
          <div>
            <p className="text-4xl font-black" style={{color:conf>=65?"#34d399":conf>=45?"#fbbf24":"#f87171"}}>{conf.toFixed(0)}/100</p>
            <p className="text-[10px] opacity-40 mt-1">group average data confidence</p>
          </div>
          <div className="flex-1 pb-2">
            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{width:conf+"%",background:conf>=65?"#34d399":conf>=45?"#fbbf24":"#f87171"}} />
            </div>
            <div className="flex justify-between text-[9px] opacity-30 mt-1"><span>0</span><span>Target ≥65 (T2 declared)</span><span>100</span></div>
          </div>
        </div>
        <p className="text-[10px] opacity-40">Required documents per entity: KYC · Utility bills · Production records · Waste records · Financial records · Site photos. Target ≥65% for VCM / ≥80% for Art.6.4.</p>
      </div>
      <SectionCard title="Entity Evidence Quality">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Subsidiary","Division","Evidence Level","Confidence Score","VCM Readiness","Priority Action"].map(h=>(
                <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
              ))}
            </tr></thead>
            <tbody>{subsidiaries.map(s=>{
              const conf_s=s.calc?.data_confidence_score||s.data_confidence_score||45;
              const ev=s.calc?.evidence_level||s.evidence_level||"estimated";
              const vcm=conf_s>=65&&["declared","measured"].includes(ev);
              return (
                <tr key={s.sub_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2.5 px-3 font-medium">{s.name}</td>
                  <td className="py-2.5 px-3 text-slate-500 capitalize">{(s.division||"").replace(/_/g," ")}</td>
                  <td className="py-2.5 px-3"><span className={`text-[9px] px-2 py-0.5 rounded font-bold capitalize ${ev==="measured"?"bg-emerald-50 text-emerald-700":ev==="declared"?"bg-blue-50 text-blue-700":"bg-amber-50 text-amber-700"}`}>{ev}</span></td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{width:conf_s+"%",background:conf_s>=65?"#059669":"#d97706"}}/></div>
                      <span className="font-black text-[10px]">{conf_s.toFixed(0)}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3">{vcm?<CheckCircle2 size={12} className="text-emerald-500"/>:<AlertTriangle size={12} className="text-amber-400"/>}</td>
                  <td className="py-2.5 px-3 text-[10px] text-slate-500">{ev==="estimated"?"Upgrade to declared T2 evidence":ev==="declared"?"Engage DOE for methodology pre-screening":"T1 evidence present — proceed to VVB"}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </SectionCard>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2"><FileText size={12}/>Required Evidence Types</p>
          {["KYC / Business Registration","Utility Bills / Fuel Records","Production / Output Records","Waste Diversion Records","Financial Records / Invoices","Site Photos / Geo-tagged Proof"].map(d=>(
            <div key={d} className="flex items-center gap-2 py-1 text-[10px] text-slate-500 border-b border-slate-50 last:border-0"><div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0"/>{d}</div>
          ))}
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2"><Shield size={12}/>VVB Pre-Qualification Path</p>
          {[["Q1–Q2 2026","Evidence upgrade to T1 measured","Ready now"],["Q2–Q3 2026","DOE engagement + methodology pre-screen","Planned"],["Q3 2026","Project Design Document drafting","Design stage"],["Q4 2026","VVB validation","Prerequisite: PDD complete"],["Q2 2027","Registry registration","Prerequisite: VVB complete"]].map(([t,a,s])=>(
            <div key={t} className="py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-[9px] font-bold text-slate-500">{t}</p>
              <p className="text-[10px] text-slate-700">{a}</p>
              <p className="text-[9px] text-slate-400">{s}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2"><Info size={12}/>SSOT Audit Trail</p>
          {["All calculations version-controlled in SSOT engine","Input changes trigger new immutable calc run","Group totals re-derived from subsidiaries (no shortcuts)","No manual overrides permitted in report layer","Report hash linked to calc state at generation time","DeCarbonIQ platform maintains full calculation history"].map(a=>(
            <div key={a} className="flex items-start gap-1.5 py-1 text-[10px] text-slate-500 border-b border-slate-50 last:border-0">
              <CheckCircle2 size={10} className="text-emerald-500 flex-shrink-0 mt-0.5"/>
              <span>{a}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
